import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    inhaltsverzeichnis,
    type Dokument,
    type Knoten,
    type KnotenLuecke,
    type KnotenTabelle,
    type KnotenUeberschrift,
    type KnotenUnterschrift,
    type Regelpunkt,
} from '../../components/bauordnung/dokument/dokumentbeschreibung';
import { FARBE, spaltenBreiten, trenneUeberlangeWoerter } from './generatorPlanungsdokumentation';
import { A4_GEOMETRY, FOOTER_RESERVE } from './letterhead';
import { sichereTextausgabe } from './winAnsi';

/**
 * Uebersetzt die Dokumentbeschreibung in ein PDF — und sonst nichts.
 *
 * Diese Datei trifft KEINE fachliche Entscheidung. Was gedruckt wird, hat
 * `baueDokument()` entschieden; hier wird nur gezeichnet. Der Grund ist ein
 * eingetretener Fehler: ein Altgenerator hat Fachlogik nachgebaut und dabei
 * den Titel eines gesperrten Dokuments gedruckt.
 *
 * ─── Gestaltung ───────────────────────────────────────────────────────────
 * Dasselbe Satzbild wie die Dokumentation der Planungsphase
 * (`generatorPlanungsdokumentation.ts`, Betreiber 31.10.2026: „ohne gefärbte
 * Kästen, wo Text drin steht"; für die Baustellenordnung 03.11.2026: „so
 * professionell wie unsere anderen Dokumente"):
 *   - eine Schriftfamilie (Helvetica), Hierarchie nur über Größe und Schnitt,
 *     nur Schwarz und Grau (`FARBE`). Keine Fläche, kein Kasten, kein Rahmen;
 *   - Tabellen mit Linien statt Flächen: kräftige Linie über und unter dem
 *     Kopf, Haarlinien zwischen den Zeilen, kräftige Linie am Ende, der Kopf
 *     wiederholt sich auf jeder Folgeseite;
 *   - Titelseite mit Titel, Untertitel, kräftiger Linie, Angabenliste,
 *     Haarlinie und Vorbemerkung; Kopfzeile ab Seite 2 mit Bauvorhaben und
 *     Titel, Fußzeile mit Stand und „Seite x von y";
 *   - ein Entwurf sagt es in der Angabenliste, in der Vorbemerkung und in der
 *     Kopfzeile („Baustellenordnung · Entwurf"), nicht in einem farbigen
 *     Balken.
 *
 * ─── Was anders ist als im Vorbild ────────────────────────────────────────
 * Herausgeber ist der ERLASSENDE (Bauherr oder Generalunternehmer, er hat das
 * Hausrecht; Spezifikation A, „Wer erlässt, wer verfasst"). Deshalb gibt es
 * KEINEN Briefkopf des Büros, kein Logo und keine Büroanschrift: oben auf der
 * Titelseite steht der Erlassende. Das verfassende Büro steht nur klein in
 * der Fußzeile jeder Seite (`verfasserZeile`), und kein Unterschriftsfeld
 * trägt es.
 *
 * ─── Inhaltsverzeichnis ───────────────────────────────────────────────────
 * Die Einträge stehen vor dem Satz fest (`inhaltsverzeichnis`). Das
 * Verzeichnis bekommt deshalb VORAB so viele Seiten, wie es braucht; der
 * Inhalt wird danach gesetzt, die Seite jeder Überschrift unter ihrer Stelle
 * in `knoten` notiert (Rahmenkapitel und Teile haben keine Nummer) und das
 * Verzeichnis zum Schluss auf die freigehaltenen Seiten geschrieben. Keine
 * Seitenzahl muss nachträglich verschoben werden.
 *
 * ─── Umbruch ──────────────────────────────────────────────────────────────
 * Eine Überschrift steht nie allein am Seitenende: sie nimmt mindestens den
 * Anfang des folgenden Blocks mit. Absätze und Regeln lassen keine einzelne
 * Zeile zurück oder vorauslaufen; die Zuständigkeitszeile einer Regel bleibt
 * bei ihr. Ein kurzer Absatz vor einer Tabelle oder einem Unterschriftsfeld
 * bleibt bei ihnen. Ein Unterschriftsfeld wird nie getrennt.
 */

// ═══════════════════════════════════════════════════════════════════════════
// SATZ
// ═══════════════════════════════════════════════════════════════════════════

const GEO = A4_GEOMETRY;
const PT = 0.3528; // mm je Punkt

type Rgb = readonly [number, number, number];

const SCHRIFT = {
    titel: 20,
    untertitel: 11.5,
    herausgeber: 11,
    h1: 12.5,
    h2: 10.5,
    h3: 10,
    text: 10,
    tabelle: 8,
    klein: 8,
    fuss: 7,
    verzeichnisTitel: 12.5,
};

const ZEILE = {
    titel: 8.6,
    untertitel: 5.6,
    titelei: 6.2,
    angabe: 5.0,
    text: 5.0,
    klein: 3.9,
    h1: 6.4,
    h2: 5.4,
    h3: 5.0,
    verzeichnis1: 6.2,
    verzeichnis2: 5.3,
    fuss: 3.3,
};

const ABSTAND = {
    absatz: 2.6,
    block: 3.5,
    regel: 2.0,
};

const UEBERSCHRIFT: Record<1 | 2 | 3, { groesse: number; zeile: number; vor: number; nach: number }> = {
    1: { groesse: SCHRIFT.h1, zeile: ZEILE.h1, vor: 7, nach: 2.6 },
    2: { groesse: SCHRIFT.h2, zeile: ZEILE.h2, vor: 4.5, nach: 1.6 },
    3: { groesse: SCHRIFT.h3, zeile: ZEILE.h3, vor: 3.5, nach: 1.2 },
};

/** Tabulator für Text hinter einer Nummer, in mm. */
const TAB = 11;

/** Einzug hinter einem Aufzählungspunkt, in mm. */
const PUNKT_EINZUG = 5;

/** Breite der Bezeichnungsspalte der Angabenliste, in mm. */
const ANGABE_BEZEICHNUNG = 48;

const TABELLE_POLSTER = { oben: 1.4, unten: 1.4, seite: 1.2 };

/** Zeilenhöhe, die autoTable bei der Tabellenschrift setzt (Faktor 1,15). */
const TABELLE_ZEILE = SCHRIFT.tabelle * 1.15 * PT;

/** Ein kurzer Absatz (bis zu so vielen Zeilen) bleibt mit der folgenden Tabelle oder Unterschrift auf einer Seite. */
const EINLEITUNG_MAX_ZEILEN = 6;

const LINIE = { kraeftig: 0.3, kopf: 0.2, haar: 0.1 };

/** Höhe eines Unterschriftsfelds; es wird nie getrennt. */
const ZEICHNUNG_HOEHE = 34;

/** Grundlinie der Kopfzeile ab Seite 2. */
const KOPF_Y = 13;

const VERZEICHNIS_KOPF = 14;

/** Grundlinie einer Textzeile, deren Zeilenkasten bei `oben` beginnt. */
const grundlinie = (oben: number, groesse: number, zeilenhoehe: number): number => {
    const mm = groesse * PT;
    return oben + (zeilenhoehe - mm) / 2 + 0.78 * mm;
};

export interface BauordnungPdfOptionen {
    /**
     * Fußzeile links, wenn das Dokument keinen Stand trägt (sonst steht dort
     * „Stand: …"). Ohne Angabe die Firma des Erlassenden.
     */
    fusszeileLinks?: string;
    /** Nur fuer Tests: erlaubt ein vorbereitetes Dokument. */
    __docFactory?: () => jsPDF;
}

interface Satz {
    doc: jsPDF;
    y: number;
    /** Oberkante des Inhalts auf Folgeseiten. */
    oben: number;
    /** Unterkante des Inhalts (der Fußzeilenstreifen bleibt frei). */
    unten: number;
    /** Stelle einer Überschrift in `dok.knoten` → Seite. */
    seiten: Map<number, number>;
}

const aktuelleSeite = (doc: jsPDF): number =>
    (doc.internal as unknown as { getCurrentPageInfo: () => { pageNumber: number } }).getCurrentPageInfo().pageNumber;

const farbe = (doc: jsPDF, f: Rgb) => doc.setTextColor(f[0], f[1], f[2]);
const linienfarbe = (doc: jsPDF, f: Rgb) => doc.setDrawColor(f[0], f[1], f[2]);

const schrift = (doc: jsPDF, stil: 'normal' | 'bold' | 'italic', groesse: number) => {
    doc.setFont('helvetica', stil);
    doc.setFontSize(groesse);
};

const umbrechen = (doc: jsPDF, text: string, breite: number): string[] =>
    doc.splitTextToSize(String(text || ''), breite) as string[];

/**
 * Umbruch für Text, dem auf der ersten Zeile etwas vorangeht („Offener Punkt
 * Nr. 3:" fett, dann der Text): die erste Zeile ist um diesen Vorlauf
 * kürzer. Liefert mindestens eine (ggf. leere) Zeile.
 */
const umbrechenMitVorlauf = (doc: jsPDF, text: string, ersteBreite: number, breite: number): string[] => {
    const woerter = String(text || '').split(/\s+/).filter(Boolean);
    const zeilen: string[] = [];
    let aktuell = '';
    let max = ersteBreite;
    for (const w of woerter) {
        const probe = aktuell ? `${aktuell} ${w}` : w;
        if (doc.getTextWidth(probe) <= max) { aktuell = probe; continue; }
        if (aktuell) {
            zeilen.push(aktuell);
            aktuell = '';
            max = breite;
        } else if (!zeilen.length && max < breite) {
            // Schon das erste Wort passt nicht hinter den Vorlauf: der
            // Vorlauf steht allein, der Text beginnt auf der zweiten Zeile.
            zeilen.push('');
            max = breite;
        }
        if (doc.getTextWidth(w) <= max) { aktuell = w; continue; }
        const stuecke = umbrechen(doc, w, max);
        zeilen.push(...stuecke.slice(0, -1));
        aktuell = stuecke[stuecke.length - 1] || '';
    }
    if (aktuell || !zeilen.length) zeilen.push(aktuell);
    return zeilen;
};

const neueSeite = (s: Satz) => {
    s.doc.addPage();
    s.y = s.oben;
};

const amSeitenkopf = (s: Satz): boolean => s.y <= s.oben + 0.01;

/**
 * Wie viele der `rest` Zeilen kommen auf diese Seite? Hurenkind- und
 * Schusterjungenregel: bricht ein Block um, stehen auf beiden Seiten
 * mindestens zwei Zeilen. `anhang` muss mit der letzten Zeile auf derselben
 * Seite stehen (Zuständigkeitszeile einer Regel). 0 heißt: neue Seite.
 */
const zeilenAufSeite = (s: Satz, rest: number, zh: number, anhang: number): number => {
    const platz = s.unten - s.y + 0.01;
    if (rest * zh + anhang <= platz) return rest;
    const frei = Math.floor(platz / zh);
    const n = Math.min(frei, rest - 2);
    if (n >= 2) return n;
    return amSeitenkopf(s) ? Math.max(1, Math.min(frei, rest)) : 0;
};

/** Setzt umbrochene Zeilen ab `s.y` und blättert nach `zeilenAufSeite` um. */
const setzeZeilen = (
    s: Satz,
    zeilen: string[],
    zh: number,
    zeichne: (zeile: string, k: number, oben: number) => void,
    anhang = 0,
) => {
    let i = 0;
    while (i < zeilen.length) {
        const n = zeilenAufSeite(s, zeilen.length - i, zh, anhang);
        if (n === 0) { neueSeite(s); continue; }
        for (let k = 0; k < n; k++) {
            zeichne(zeilen[i + k], i + k, s.y);
            s.y += zh;
        }
        i += n;
        if (i < zeilen.length) neueSeite(s);
    }
};

// ── Höhen für den Umbruch ─────────────────────────────────────────────────

/** Abstand vom Zeilenanfang bis zum Text hinter einer Nummer. */
const nummerEinzug = (doc: jsPDF, nummer: string | undefined): number =>
    (nummer ? Math.max(TAB, doc.getTextWidth(nummer) + 3) : 0);

const ueberschriftSatz = (s: Satz, k: KnotenUeberschrift): { zeilen: string[]; einzug: number } => {
    schrift(s.doc, 'bold', UEBERSCHRIFT[k.ebene].groesse);
    const einzug = nummerEinzug(s.doc, k.nummer);
    return { zeilen: umbrechen(s.doc, k.text, GEO.contentWidth - einzug), einzug };
};

const ueberschriftHoehe = (s: Satz, k: KnotenUeberschrift, mitVorlauf: boolean): number => {
    const m = UEBERSCHRIFT[k.ebene];
    return (mitVorlauf ? m.vor : 0) + ueberschriftSatz(s, k).zeilen.length * m.zeile + m.nach;
};

const absatzZeilen = (s: Satz, text: string, stil: 'normal' | 'italic' = 'normal'): string[] => {
    schrift(s.doc, stil, SCHRIFT.text);
    return umbrechen(s.doc, text, GEO.contentWidth);
};

const spaltenVon = (k: KnotenTabelle) =>
    spaltenBreiten(k.kopf.map((titel, i) => ({ titel, ...(k.spalten?.[i] || {}) })));

/** Kurze Tabellen (bis so viele Zeilen) werden nicht getrennt, wenn sie auf eine Seite passen. */
const TABELLE_ZUSAMMEN = 6;

const tabellenZeilenHoehe = (s: Satz, k: KnotenTabelle, zellen: string[], stil: 'normal' | 'bold'): number => {
    const breiten = spaltenVon(k);
    schrift(s.doc, stil, SCHRIFT.tabelle);
    const zeilen = zellen.reduce((max, z, i) => {
        const verfuegbar = Math.max(4, (breiten[i] || 20) - 2 * TABELLE_POLSTER.seite);
        return Math.max(max, umbrechen(s.doc, z, verfuegbar).length);
    }, 1);
    return zeilen * TABELLE_ZEILE + TABELLE_POLSTER.oben + TABELLE_POLSTER.unten;
};

/**
 * Was von einer Tabelle mindestens zusammen auf eine Seite muss: eine kurze
 * Tabelle ganz (eine einzelne Zeile unter dem wiederholten Kopf auf der
 * Folgeseite sieht nach Versehen aus), sonst Kopf und erste Zeile.
 */
const tabellenAnfangHoehe = (s: Satz, k: KnotenTabelle): number => {
    const kopf = tabellenZeilenHoehe(s, k, k.kopf, 'bold');
    const zeilen = k.zeilen.map(z => tabellenZeilenHoehe(s, k, z, 'normal'));
    const ganz = kopf + zeilen.reduce((a, b) => a + b, 0) + 1;
    if (k.zeilen.length <= TABELLE_ZUSAMMEN && ganz <= s.unten - s.oben) return ganz;
    // Eine sehr hohe erste Zeile wird ohnehin umbrochen; dann genügt ein Anfang.
    return kopf + Math.min(zeilen[0] ?? 0, 40) + 1;
};

/** Einzug hinter Nummer oder Punkt für alle Punkte eines Regelblocks. */
const regelEinzug = (s: Satz, punkte: readonly Regelpunkt[]): number => {
    schrift(s.doc, 'normal', SCHRIFT.text);
    const nummern = punkte.map(p => p.nummer || '').filter(Boolean);
    if (!nummern.length) return PUNKT_EINZUG;
    return Math.max(TAB, ...nummern.map(n => s.doc.getTextWidth(n) + 3));
};

const regelZeilen = (s: Satz, p: Regelpunkt, einzug: number): string[] => {
    schrift(s.doc, 'normal', SCHRIFT.text);
    return umbrechen(s.doc, p.text, GEO.contentWidth - einzug);
};

const zustaendigkeit = (p: Regelpunkt): string =>
    [p.verantwortlich ? `Verantwortlich: ${p.verantwortlich}` : '', p.rhythmus ? `Rhythmus: ${p.rhythmus}` : '']
        .filter(Boolean)
        .join(' · ');

const zustaendigkeitZeilen = (s: Satz, p: Regelpunkt, einzug: number): string[] => {
    const text = zustaendigkeit(p);
    if (!text) return [];
    schrift(s.doc, 'normal', SCHRIFT.klein);
    return umbrechen(s.doc, text, GEO.contentWidth - einzug);
};

const LUECKE_KOPF = (nr: number) => `Offener Punkt Nr. ${nr}:`;

const lueckeSatz = (s: Satz, k: KnotenLuecke): { kopf: string; kopfBreite: number; zeilen: string[] } => {
    const breite = GEO.contentWidth - TAB;
    const kopf = LUECKE_KOPF(k.nr);
    schrift(s.doc, 'bold', SCHRIFT.text);
    const kopfBreite = s.doc.getTextWidth(kopf);
    schrift(s.doc, 'normal', SCHRIFT.text);
    const vorlauf = kopfBreite + s.doc.getTextWidth(' ');
    return { kopf, kopfBreite: vorlauf, zeilen: umbrechenMitVorlauf(s.doc, k.text, breite - vorlauf, breite) };
};

/**
 * Ein kurzer Absatz vor einer Tabelle oder einem Unterschriftsfeld gehört zu
 * ihnen: liefert die Höhe von Absatz plus Anfang des Folgenden, sonst `null`.
 */
const mitFolgeHoehe = (s: Satz, knoten: readonly Knoten[], i: number): number | null => {
    const k = knoten[i];
    const naechster = knoten[i + 1];
    if (!k || k.art !== 'absatz' || !naechster) return null;
    if (naechster.art !== 'tabelle' && naechster.art !== 'unterschriftsfeld') return null;
    const zeilen = absatzZeilen(s, k.text, k.betont ? 'italic' : 'normal').length;
    if (zeilen > EINLEITUNG_MAX_ZEILEN) return null;
    const folge = naechster.art === 'tabelle' ? tabellenAnfangHoehe(s, naechster) : ZEICHNUNG_HOEHE;
    return zeilen * ZEILE.text + ABSTAND.absatz + folge;
};

/** Was nach einer Überschrift mindestens mit auf die Seite muss. */
const anfangHoehe = (s: Satz, knoten: readonly Knoten[], i: number, tiefe = 0): number => {
    const k = knoten[i];
    if (!k || tiefe > 3) return 0;
    switch (k.art) {
        case 'absatz':
        case 'hinweis':
        case 'entwurfsvermerk':
        case 'leerbefund': {
            const gebunden = mitFolgeHoehe(s, knoten, i);
            if (gebunden !== null) return gebunden;
            const stil = k.art === 'leerbefund' || (k.art === 'absatz' && k.betont) ? 'italic' : 'normal';
            return Math.min(absatzZeilen(s, k.text, stil).length, 2) * ZEILE.text;
        }
        case 'liste':
            return k.punkte.length ? Math.min(regelZeilen(s, { text: k.punkte[0] }, PUNKT_EINZUG).length, 2) * ZEILE.text : 0;
        case 'regeln': {
            const p = k.punkte[0];
            if (!p) return 0;
            const einzug = regelEinzug(s, k.punkte);
            const zeilen = regelZeilen(s, p, einzug).length;
            return Math.min(zeilen, 2) * ZEILE.text
                + (zeilen <= 2 ? zustaendigkeitZeilen(s, p, einzug).length * ZEILE.klein : 0);
        }
        case 'luecke':
            return Math.min(lueckeSatz(s, k).zeilen.length, 2) * ZEILE.text;
        case 'tabelle':
            return tabellenAnfangHoehe(s, k);
        case 'unterschriftsfeld':
            return ZEICHNUNG_HOEHE;
        case 'ueberschrift':
            return ueberschriftHoehe(s, k, true) + anfangHoehe(s, knoten, i + 1, tiefe + 1);
        default:
            return 0;
    }
};

// ── Zeichnen ──────────────────────────────────────────────────────────────

const zeichneUeberschrift = (s: Satz, knoten: readonly Knoten[], i: number, k: KnotenUeberschrift) => {
    const { doc } = s;
    const m = UEBERSCHRIFT[k.ebene];
    const bedarf = ueberschriftHoehe(s, k, !amSeitenkopf(s)) + anfangHoehe(s, knoten, i + 1);
    if (s.y + bedarf > s.unten && !amSeitenkopf(s)) neueSeite(s);
    if (!amSeitenkopf(s)) s.y += m.vor;

    s.seiten.set(i, aktuelleSeite(doc));
    const { zeilen, einzug } = ueberschriftSatz(s, k);
    farbe(doc, FARBE.text);
    schrift(doc, 'bold', m.groesse);
    if (k.nummer) doc.text(k.nummer, GEO.margin, grundlinie(s.y, m.groesse, m.zeile));
    zeilen.forEach(z => {
        doc.text(z, GEO.margin + einzug, grundlinie(s.y, m.groesse, m.zeile));
        s.y += m.zeile;
    });
    s.y += m.nach;
};

const zeichneAbsatz = (s: Satz, text: string, stil: 'normal' | 'italic' = 'normal', f: Rgb = FARBE.text) => {
    const { doc } = s;
    setzeZeilen(s, absatzZeilen(s, text, stil), ZEILE.text, (z, _k, oben) => {
        schrift(doc, stil, SCHRIFT.text);
        farbe(doc, f);
        doc.text(z, GEO.margin, grundlinie(oben, SCHRIFT.text, ZEILE.text));
    });
    farbe(doc, FARBE.text);
    s.y += ABSTAND.absatz;
};

/**
 * Regeln mit hängendem Einzug: Nummer („7.1") oder Punkt links, der Wortlaut
 * in einer Flucht daneben, darunter klein und grau Verantwortlich und
 * Rhythmus. Nummern zählt `baueDokument`, nicht diese Funktion.
 */
const zeichneRegeln = (s: Satz, punkte: readonly Regelpunkt[]) => {
    const { doc } = s;
    const einzug = regelEinzug(s, punkte);
    punkte.forEach(p => {
        const zeilen = regelZeilen(s, p, einzug);
        const zustaendig = zustaendigkeitZeilen(s, p, einzug);
        const anhang = zustaendig.length ? zustaendig.length * ZEILE.klein + 0.6 : 0;
        setzeZeilen(s, zeilen, ZEILE.text, (z, k, oben) => {
            const y = grundlinie(oben, SCHRIFT.text, ZEILE.text);
            schrift(doc, 'normal', SCHRIFT.text);
            farbe(doc, FARBE.text);
            if (k === 0) doc.text(p.nummer || '•', GEO.margin + (p.nummer ? 0 : 0.8), y);
            doc.text(z, GEO.margin + einzug, y);
        }, anhang);
        if (zustaendig.length) {
            s.y += 0.6;
            schrift(doc, 'normal', SCHRIFT.klein);
            farbe(doc, FARBE.grau);
            zustaendig.forEach(z => {
                doc.text(z, GEO.margin + einzug, grundlinie(s.y, SCHRIFT.klein, ZEILE.klein));
                s.y += ZEILE.klein;
            });
            farbe(doc, FARBE.text);
        }
        s.y += ABSTAND.regel;
    });
    s.y += ABSTAND.block - ABSTAND.regel;
};

/** Offener Punkt an der Textstelle: eingerückt, „Offener Punkt Nr. x:" fett, der Text normal. Kein Kasten. */
const zeichneLuecke = (s: Satz, k: KnotenLuecke) => {
    const { doc } = s;
    const x = GEO.margin + TAB;
    const { kopf, kopfBreite, zeilen } = lueckeSatz(s, k);
    setzeZeilen(s, zeilen, ZEILE.text, (z, i, oben) => {
        const y = grundlinie(oben, SCHRIFT.text, ZEILE.text);
        farbe(doc, FARBE.text);
        if (i === 0) {
            schrift(doc, 'bold', SCHRIFT.text);
            doc.text(kopf, x, y);
            schrift(doc, 'normal', SCHRIFT.text);
            if (z) doc.text(z, x + kopfBreite, y);
            return;
        }
        schrift(doc, 'normal', SCHRIFT.text);
        doc.text(z, x, y);
    });
    s.y += ABSTAND.absatz;
};

const zeichneTabelle = (s: Satz, k: KnotenTabelle) => {
    const { doc } = s;
    const breiten = spaltenVon(k);
    const letzte = k.kopf.length - 1;
    const letzteZeile = k.zeilen.length - 1;
    if (s.y + tabellenAnfangHoehe(s, k) > s.unten && !amSeitenkopf(s)) neueSeite(s);

    const polster = (spalte: number) => ({
        top: TABELLE_POLSTER.oben,
        bottom: TABELLE_POLSTER.unten,
        // Erste und letzte Spalte bündig mit dem Satzspiegel: die Linien
        // laufen über die volle Breite, der Text steht in der Flucht des
        // Fließtexts.
        left: spalte === 0 ? 0 : TABELLE_POLSTER.seite,
        right: spalte === letzte ? 0 : TABELLE_POLSTER.seite,
    });
    const columnStyles: Record<number, { cellWidth: number }> = {};
    k.kopf.forEach((_, i) => { columnStyles[i] = { cellWidth: breiten[i] }; });

    autoTable(doc, {
        startY: s.y,
        head: [k.kopf],
        body: k.zeilen.map(z => k.kopf.map((_, i) => String(z[i] ?? ''))),
        theme: 'plain',
        // Der Kopf wiederholt sich auf jeder Folgeseite — ohne ihn ist eine
        // über zwei Seiten laufende Tabelle unlesbar.
        showHead: 'everyPage',
        rowPageBreak: 'avoid',
        tableWidth: GEO.contentWidth,
        margin: { top: s.oben, left: GEO.margin, right: GEO.margin, bottom: GEO.pageHeight - s.unten },
        styles: {
            font: 'helvetica',
            fontSize: SCHRIFT.tabelle,
            textColor: FARBE.text,
            fillColor: false,
            lineColor: FARBE.haar,
            lineWidth: { bottom: LINIE.haar },
            valign: 'top',
            overflow: 'linebreak',
        },
        headStyles: {
            fontStyle: 'bold',
            fillColor: false,
            textColor: FARBE.text,
            lineColor: FARBE.linie,
            lineWidth: { top: LINIE.kraeftig, bottom: LINIE.kopf },
        },
        columnStyles,
        didParseCell: (data) => {
            // Polster hier statt in `columnStyles`: die gelten bei autoTable
            // nur für den Rumpf, der Kopf stünde sonst eingerückt.
            const p = polster(data.column.index);
            data.cell.styles.cellPadding = p;
            // Dieselbe Toleranz wie autoTable beim Umbruch (1 pt): was dort
            // ungetrennt passt, bekommt hier keinen Trennstrich.
            const verfuegbar = (breiten[data.column.index] || 20) - p.left - p.right + PT;
            schrift(doc, data.cell.styles.fontStyle === 'bold' ? 'bold' : 'normal', SCHRIFT.tabelle);
            const messen = (t: string) => doc.getTextWidth(t);
            data.cell.text = (data.cell.text || [])
                .map(z => trenneUeberlangeWoerter(z, verfuegbar, messen))
                .flatMap(z => z.split('\n'));
            if (data.section === 'body' && data.row.index === letzteZeile) {
                data.cell.styles.lineWidth = { bottom: LINIE.kraeftig };
                data.cell.styles.lineColor = FARBE.linie;
            }
        },
    });
    const ende = (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable;
    s.y = (ende && typeof ende.finalY === 'number' ? ende.finalY : s.y) + ABSTAND.block + 1;
    // autoTable stellt Schrift und Farben nicht zurück.
    farbe(doc, FARBE.text);
};

/**
 * Unterschriftsfelder als Linie mit Beschriftung darunter: links „Ort,
 * Datum", rechts die Unterschrift mit Name und darunter Funktion und Firma.
 * Die Zeilen tragen nur den Erlassenden und beim Kenntnisnahmeblatt den
 * gegenzeichnenden Auftragnehmer, nie den Verfasser (`baueDokument`).
 */
const zeichneUnterschriften = (s: Satz, k: KnotenUnterschrift) => {
    const { doc } = s;
    const breite = 75;
    k.zeilen.forEach(zeile => {
        // Hoehe aus der tatsaechlichen Zeilenzahl der Beschriftung: Linie bei
        // +18, Name +4,2 und +4,4, je Beschriftungszeile 3,6, Luft 4.
        schrift(doc, 'normal', SCHRIFT.klein);
        const beschriftungZeilen = umbrechen(doc, [zeile.rolle, zeile.firma].filter(Boolean).join(', ') || 'Unterschrift', breite).length;
        const hoehe = Math.max(ZEICHNUNG_HOEHE, 18 + 4.2 + 4.4 + 3.6 * beschriftungZeilen + 4);
        if (s.y + hoehe > s.unten && !amSeitenkopf(s)) neueSeite(s);
        const linieY = s.y + 18;
        const felder = [
            { x: GEO.margin, name: '', beschriftung: 'Ort, Datum' },
            {
                x: GEO.rightX - breite,
                name: zeile.name,
                beschriftung: [zeile.rolle, zeile.firma].filter(Boolean).join(', ') || 'Unterschrift',
            },
        ];
        let unterkante = linieY + 16;
        felder.forEach(f => {
            linienfarbe(doc, FARBE.linie);
            doc.setLineWidth(LINIE.kopf);
            doc.line(f.x, linieY, f.x + breite, linieY);
            let ty = linieY + 4.2;
            if (f.name) {
                schrift(doc, 'normal', SCHRIFT.text - 0.5);
                farbe(doc, FARBE.text);
                doc.text(f.name, f.x, ty);
                ty += 4.4;
            }
            schrift(doc, 'normal', SCHRIFT.klein);
            farbe(doc, FARBE.grau);
            // Funktion und Firma vollstaendig: abgeschnitten stuende unter
            // der Unterschrift eine halbe Firmierung.
            umbrechen(doc, f.beschriftung, breite).forEach(t => {
                doc.text(t, f.x, ty);
                ty += 3.6;
            });
            unterkante = Math.max(unterkante, ty + 4);
        });
        farbe(doc, FARBE.text);
        s.y = unterkante;
    });
};

const zeichneInhalt = (s: Satz, knoten: readonly Knoten[]) => {
    knoten.forEach((k, i) => {
        switch (k.art) {
            case 'seitenumbruch':
                if (!amSeitenkopf(s)) neueSeite(s);
                break;
            case 'ueberschrift':
                zeichneUeberschrift(s, knoten, i, k);
                break;
            case 'absatz': {
                const gebunden = mitFolgeHoehe(s, knoten, i);
                if (gebunden !== null && s.y + gebunden > s.unten && !amSeitenkopf(s)) neueSeite(s);
                zeichneAbsatz(s, k.text, k.betont ? 'italic' : 'normal');
                break;
            }
            case 'hinweis':
            case 'entwurfsvermerk':
                zeichneAbsatz(s, k.text);
                break;
            case 'leerbefund':
                zeichneAbsatz(s, k.text, 'italic', FARBE.grau);
                break;
            case 'liste':
                zeichneRegeln(s, k.punkte.map(text => ({ text })));
                break;
            case 'regeln':
                zeichneRegeln(s, k.punkte);
                break;
            case 'tabelle':
                zeichneTabelle(s, k);
                break;
            case 'luecke':
                zeichneLuecke(s, k);
                break;
            case 'unterschriftsfeld':
                zeichneUnterschriften(s, k);
                break;
            default:
                break;
        }
    });
};

// ── Titelseite ────────────────────────────────────────────────────────────

const zeichneTitelseite = (s: Satz, dok: Dokument) => {
    const { doc } = s;
    s.y = s.oben;

    // Oben steht, wer erlässt: kein Briefkopf des Büros.
    if (dok.herausgeber.firma) {
        schrift(doc, 'normal', SCHRIFT.klein);
        farbe(doc, FARBE.grau);
        doc.text('Erlassen durch', GEO.margin, grundlinie(s.y, SCHRIFT.klein, ZEILE.klein));
        s.y += ZEILE.klein + 0.8;
        schrift(doc, 'bold', SCHRIFT.herausgeber);
        farbe(doc, FARBE.text);
        umbrechen(doc, dok.herausgeber.firma, GEO.contentWidth * 0.7).forEach(z => {
            doc.text(z, GEO.margin, grundlinie(s.y, SCHRIFT.herausgeber, ZEILE.untertitel));
            s.y += ZEILE.untertitel;
        });
        if (dok.herausgeber.rolle) {
            schrift(doc, 'normal', SCHRIFT.text);
            farbe(doc, FARBE.grau);
            doc.text(dok.herausgeber.rolle, GEO.margin, grundlinie(s.y, SCHRIFT.text, ZEILE.text));
            s.y += ZEILE.text;
        }
    }

    s.y = Math.max(s.y + 24, 78);
    farbe(doc, FARBE.text);
    schrift(doc, 'bold', SCHRIFT.titel);
    umbrechen(doc, dok.titel, GEO.contentWidth).forEach(z => {
        doc.text(z, GEO.margin, grundlinie(s.y, SCHRIFT.titel, ZEILE.titel));
        s.y += ZEILE.titel;
    });
    s.y += 1.5;
    schrift(doc, 'normal', SCHRIFT.untertitel);
    umbrechen(doc, dok.untertitel, GEO.contentWidth).forEach(z => {
        doc.text(z, GEO.margin, grundlinie(s.y, SCHRIFT.untertitel, ZEILE.untertitel));
        s.y += ZEILE.untertitel;
    });

    s.y += 9;
    linienfarbe(doc, FARBE.linie);
    doc.setLineWidth(LINIE.kraeftig);
    doc.line(GEO.margin, s.y, GEO.rightX, s.y);
    s.y += 4;

    dok.deckblatt.forEach((z, i) => {
        // Die erste Angabe (das Bauvorhaben) ist die Hauptsache.
        const stil = i === 0 ? 'bold' : 'normal';
        schrift(doc, 'normal', SCHRIFT.text);
        const links = umbrechen(doc, z.merkmal, ANGABE_BEZEICHNUNG - 3);
        schrift(doc, stil, SCHRIFT.text);
        const rechts = umbrechen(doc, z.angabe, GEO.contentWidth - ANGABE_BEZEICHNUNG);
        // Ein langer Geltungsbereich darf nicht in die Fusszeile laufen.
        if (s.y + Math.max(links.length, rechts.length) * ZEILE.angabe > s.unten) neueSeite(s);
        schrift(doc, 'normal', SCHRIFT.text);
        farbe(doc, FARBE.grau);
        links.forEach((t, k) => doc.text(t, GEO.margin, grundlinie(s.y + k * ZEILE.angabe, SCHRIFT.text, ZEILE.angabe)));
        schrift(doc, stil, SCHRIFT.text);
        farbe(doc, FARBE.text);
        rechts.forEach((t, k) => doc.text(t, GEO.margin + ANGABE_BEZEICHNUNG, grundlinie(s.y + k * ZEILE.angabe, SCHRIFT.text, ZEILE.angabe)));
        s.y += Math.max(links.length, rechts.length) * ZEILE.angabe + (ZEILE.titelei - ZEILE.angabe);
    });

    s.y += 2;
    linienfarbe(doc, FARBE.linie);
    doc.setLineWidth(LINIE.haar);
    doc.line(GEO.margin, s.y, GEO.rightX, s.y);
    s.y += 10;

    if (dok.vorbemerkung.length) {
        if (s.y + 30 > s.unten) neueSeite(s);
        const m = UEBERSCHRIFT[2];
        schrift(doc, 'bold', m.groesse);
        farbe(doc, FARBE.text);
        doc.text('Vorbemerkung', GEO.margin, grundlinie(s.y, m.groesse, m.zeile));
        s.y += m.zeile + m.nach;
        dok.vorbemerkung.forEach(t => zeichneAbsatz(s, t));
    }
};

// ── Inhaltsverzeichnis ────────────────────────────────────────────────────

interface VerzeichnisZeile {
    index: number;
    nummer: string;
    zeilen: string[];
    stil: 'normal' | 'bold';
    zh: number;
    nummerX: number;
    textX: number;
    /** Abstand vor dem Eintrag (vor einem Hauptabschnitt, nicht vor dem ersten). */
    vor: number;
    hoehe: number;
}

const verzeichnisZeilen = (s: Satz, dok: Dokument): VerzeichnisZeile[] => {
    const eintraege = inhaltsverzeichnis(dok);
    // Zwei Stufen (HSE-Plan: Teile und Kapitel) wie im Vorbild: die oberste
    // fett. Hat das Verzeichnis nur eine Stufe (Baustellenordnung), steht
    // alles in Grundschrift; eine Seite voller fetter Zeilen hebt nichts hervor.
    const zweiStufen = eintraege.some(e => e.ueberschrift.ebene === 2) && eintraege.some(e => e.ueberschrift.ebene === 1);
    return eintraege.map(({ index, ueberschrift: u }, i) => {
        const oben = u.ebene === 1;
        const stil = zweiStufen && oben ? 'bold' : 'normal';
        const zh = oben ? ZEILE.verzeichnis1 : ZEILE.verzeichnis2;
        schrift(s.doc, stil, SCHRIFT.text);
        const nummerX = GEO.margin + (oben ? 0 : TAB);
        const textX = nummerX + nummerEinzug(s.doc, u.nummer);
        const zeilen = umbrechen(s.doc, u.text, GEO.rightX - textX - 16);
        const vor = zweiStufen && oben && i > 0 ? 2 : 0;
        return { index, nummer: u.nummer || '', zeilen, stil, zh, nummerX, textX, vor, hoehe: vor + zeilen.length * zh };
    });
};

/** Verteilt die Verzeichniszeilen auf Seiten — dieselbe Rechnung für das Freihalten und das Schreiben. */
const verteileVerzeichnis = (s: Satz, zeilen: VerzeichnisZeile[]): VerzeichnisZeile[][] => {
    const seiten: VerzeichnisZeile[][] = [[]];
    let y = s.oben + VERZEICHNIS_KOPF;
    zeilen.forEach(z => {
        if (y + z.hoehe > s.unten && seiten[seiten.length - 1].length) {
            seiten.push([]);
            y = s.oben;
        }
        seiten[seiten.length - 1].push(z);
        y += z.hoehe;
    });
    return seiten;
};

const zeichneVerzeichnis = (s: Satz, seiten: VerzeichnisZeile[][], ersteSeite: number) => {
    const { doc } = s;
    seiten.forEach((liste, si) => {
        doc.setPage(ersteSeite + si);
        let y = s.oben;
        if (si === 0) {
            schrift(doc, 'bold', SCHRIFT.verzeichnisTitel);
            farbe(doc, FARBE.text);
            doc.text('Inhalt', GEO.margin, grundlinie(y, SCHRIFT.verzeichnisTitel, ZEILE.h1));
            y += VERZEICHNIS_KOPF;
        }
        liste.forEach((z, zi) => {
            if (zi > 0) y += z.vor;
            farbe(doc, FARBE.text);
            schrift(doc, z.stil, SCHRIFT.text);
            if (z.nummer) doc.text(z.nummer, z.nummerX, grundlinie(y, SCHRIFT.text, z.zh));
            z.zeilen.forEach((t, k) => doc.text(t, z.textX, grundlinie(y + k * z.zh, SCHRIFT.text, z.zh)));
            const letzteY = y + (z.zeilen.length - 1) * z.zh;
            const seite = String(s.seiten.get(z.index) ?? '');
            doc.text(seite, GEO.rightX, grundlinie(letzteY, SCHRIFT.text, z.zh), { align: 'right' });

            // Führungspunkte zwischen Titel und Seitenzahl.
            const textEnde = z.textX + doc.getTextWidth(z.zeilen[z.zeilen.length - 1] || '');
            const zahlAnfang = GEO.rightX - doc.getTextWidth(seite);
            schrift(doc, 'normal', SCHRIFT.text);
            const schritt = doc.getTextWidth('. ');
            const anzahl = Math.floor((zahlAnfang - textEnde - 3) / schritt);
            if (anzahl > 2) {
                farbe(doc, FARBE.grau);
                doc.text('. '.repeat(anzahl).trimEnd(), zahlAnfang - 2, grundlinie(letzteY, SCHRIFT.text, z.zh), { align: 'right' });
            }
            y += z.zeilen.length * z.zh;
        });
    });
    farbe(doc, FARBE.text);
};

// ── Kopf- und Fußzeilen ───────────────────────────────────────────────────

const kuerzeAufBreite = (doc: jsPDF, text: string, breite: number): string => {
    if (doc.getTextWidth(text) <= breite) return text;
    let t = text;
    while (t.length > 1 && doc.getTextWidth(`${t}…`) > breite) t = t.slice(0, -1);
    return `${t.trimEnd()}…`;
};

const zeichneKopfUndFuss = (s: Satz, dok: Dokument, linksOhneStand: string) => {
    const { doc } = s;
    const { pageHeight, margin, rightX, pageWidth } = GEO;
    const gesamt = doc.getNumberOfPages();
    const kopfRechts = dok.entwurf ? `${dok.titel} · Entwurf` : dok.titel;
    const fussLinks = dok.standDatum ? `Stand: ${dok.standDatum}` : linksOhneStand;
    schrift(doc, 'normal', SCHRIFT.fuss);
    const verfasser = dok.verfasserZeile ? umbrechen(doc, dok.verfasserZeile, GEO.contentWidth).slice(0, 2) : [];

    for (let i = 1; i <= gesamt; i++) {
        doc.setPage(i);

        if (i >= 2) {
            schrift(doc, 'normal', SCHRIFT.klein);
            farbe(doc, FARBE.grau);
            const rechtsBreite = doc.getTextWidth(kopfRechts);
            if (dok.bauvorhaben) {
                doc.text(kuerzeAufBreite(doc, dok.bauvorhaben, GEO.contentWidth - rechtsBreite - 8), margin, KOPF_Y);
            }
            doc.text(kopfRechts, rightX, KOPF_Y, { align: 'right' });
            linienfarbe(doc, FARBE.haar);
            doc.setLineWidth(LINIE.haar);
            doc.line(margin, KOPF_Y + 2, rightX, KOPF_Y + 2);
        }

        schrift(doc, 'normal', SCHRIFT.klein);
        farbe(doc, FARBE.grau);
        const seite = `Seite ${i} von ${gesamt}`;
        if (fussLinks) {
            doc.text(kuerzeAufBreite(doc, fussLinks, GEO.contentWidth - doc.getTextWidth(seite) - 8), margin, pageHeight - 20);
        }
        doc.text(seite, rightX, pageHeight - 20, { align: 'right' });
        linienfarbe(doc, FARBE.haar);
        doc.setLineWidth(LINIE.haar);
        doc.line(margin, pageHeight - 15, rightX, pageHeight - 15);

        // Der Verfasser steht KLEIN in der Fußzeile und nirgends im Kopf:
        // erlassen wird das Dokument vom Auftraggeber, verfasst nur.
        if (verfasser.length) {
            schrift(doc, 'normal', SCHRIFT.fuss);
            verfasser.forEach((z, k) => doc.text(z, pageWidth / 2, pageHeight - 10 + k * ZEILE.fuss, { align: 'center' }));
        }
        farbe(doc, FARBE.text);
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// EINSTIEG
// ═══════════════════════════════════════════════════════════════════════════

export const renderBauordnungPdf = (
    dok: Dokument,
    optionen: BauordnungPdfOptionen = {},
): jsPDF => {
    const doc = optionen.__docFactory
        ? optionen.__docFactory()
        : new jsPDF({ format: 'a4', unit: 'mm', orientation: 'portrait', compress: true });

    // Letzte Rueckfallebene gegen UTF-16-Salat bei Umlauten. Am Dokument,
    // nicht an der einzelnen Ausgabe — eine vergessene Stelle genuegt sonst.
    sichereTextausgabe(doc);
    // autoTable setzt vor jeder Zelle die AKTUELLE Füllfarbe neu. Die Vorgabe
    // von jsPDF ist Schwarz; Weiß stellt sicher, dass nirgends eine Fläche
    // entstehen kann.
    doc.setFillColor(FARBE.papier[0], FARBE.papier[1], FARBE.papier[2]);
    doc.setProperties({
        title: [dok.titel, dok.bauvorhaben].filter(Boolean).join(', '),
        subject: dok.untertitel,
        ...(dok.herausgeber.firma ? { author: dok.herausgeber.firma } : {}),
    });

    const s: Satz = {
        doc,
        y: 0,
        oben: GEO.margin + 5,
        unten: GEO.pageHeight - FOOTER_RESERVE,
        seiten: new Map(),
    };

    zeichneTitelseite(s, dok);

    // Verzeichnisseiten freihalten.
    const zeilen = verzeichnisZeilen(s, dok);
    const verzeichnis = zeilen.length ? verteileVerzeichnis(s, zeilen) : [];
    const ersteVerzeichnisSeite = doc.getNumberOfPages() + 1;
    verzeichnis.forEach(() => doc.addPage());

    // Inhalt auf eigener Seite.
    neueSeite(s);
    zeichneInhalt(s, dok.knoten);

    zeichneVerzeichnis(s, verzeichnis, ersteVerzeichnisSeite);
    zeichneKopfUndFuss(s, dok, optionen.fusszeileLinks || dok.herausgeber.firma);
    return doc;
};
